import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft, Users, Kanban, Plus, X } from 'lucide-react';
import { useJobs, useJobCandidates, useAddCandidate } from '@/api/queries/recruitment.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { cn } from '@/lib/cn';

const STAGE_COLORS = { APPLIED:'blue', SCREENED:'purple', INTERVIEW:'yellow', OFFERED:'orange', HIRED:'green', REJECTED:'red' };

function AddCandidateModal({ jobId, onClose }) {
  const addCandidate = useAddCandidate(jobId);
  const [form, setForm] = useState({ name: '', email: '', phone: '', experience: '', source: 'Direct' });
  const [error, setError] = useState('');
  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]";
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name required'); return; }
    try {
      await addCandidate.mutateAsync({ ...form, job_id: jobId });
      onClose();
    } catch { setError('Failed to add candidate'); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-sm border border-[var(--border)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[18px] font-bold text-[var(--text-primary)]">Add Candidate</h2>
          <button onClick={onClose} className="p-1.5 rounded-[6px] hover:bg-[var(--bg-muted)]"><X size={15}/></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-3">
          {[['name','Full Name',true],['email','Email',false],['phone','Phone',false],['experience','Experience (e.g. 3 years)',false]].map(([k,label,req]) => (
            <div key={k} className="space-y-1">
              <label className="text-[12px] font-semibold text-[var(--text-primary)]">{label}{req && <span className="text-[var(--danger)]">*</span>}</label>
              <input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} className={inputCls}/>
            </div>
          ))}
          {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
          <div className="flex gap-2 pt-1">
            <Button type="submit" loading={addCandidate?.isPending} className="flex-1">Add Candidate</Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { data: jobsResp } = useJobs();
  const { data: candidatesResp } = useJobCandidates(jobId);
  const [showAdd, setShowAdd] = useState(false);

  const job = jobsResp?.data?.find(j => j.id === jobId);
  const candidates = candidatesResp?.data || [];

  if (!job) return <div className="card text-center py-16 text-[14px] text-[var(--text-tertiary)]">Job not found</div>;

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title={job.title}
        subtitle={`${job.location} · ${job.employment_type?.replace('_',' ')}`}
        breadcrumb={[{ label: 'Recruitment', href: '/recruitment' }, { label: job.title }]}
        actions={
          <div className="flex gap-2">
            <Button size="sm" icon={Kanban} variant="outline" onClick={() => navigate(`/recruitment/${jobId}/pipeline`)}>Pipeline View</Button>
            <Button size="sm" icon={Plus} onClick={() => setShowAdd(true)}>Add Candidate</Button>
            <button onClick={() => navigate('/recruitment')} className="flex items-center gap-1.5 text-[13px] text-[var(--text-secondary)]"><ArrowLeft size={15}/> Back</button>
          </div>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Object.entries(job.pipeline || {}).map(([stage, count]) => (
          <div key={stage} className="card text-center p-5">
            <p className="text-[20px] font-black text-[var(--text-primary)]">{count}</p>
            <p className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary)] mt-0.5">{stage}</p>
          </div>
        ))}
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full">
          <thead><tr className="border-b border-[var(--border)]">
            {['Candidate','Experience','Stage','Applied','Notes',''].map(h => (
              <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
            ))}
          </tr></thead>
          <tbody className="divide-y divide-[var(--border)]">
            {candidates.map(c => (
              <tr key={c.id} className="hover:bg-[var(--bg-muted)]">
                <td className="px-5 py-3.5">
                  <p className="text-[13px] font-semibold text-[var(--text-primary)]">{c.name}</p>
                  <p className="text-[11px] text-[var(--text-tertiary)]">{c.email} {c.phone ? `· ${c.phone}` : ''}</p>
                </td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{c.experience || '—'}</td>
                <td className="px-5 py-3.5"><Badge variant={STAGE_COLORS[c.stage] || 'default'} size="sm">{c.stage}</Badge></td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{c.applied_on}</td>
                <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)] max-w-[180px] truncate">{c.notes || '—'}</td>
                <td className="px-5 py-3.5">
                  <button onClick={() => navigate(`/recruitment/${jobId}/pipeline`)} className="text-[11px] font-semibold text-[var(--accent)] hover:underline">Move Stage</button>
                </td>
              </tr>
            ))}
            {candidates.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">No candidates yet — add the first one</td></tr>}
          </tbody>
        </table>
      </div>
      {showAdd && <AddCandidateModal jobId={jobId} onClose={() => setShowAdd(false)}/>}
    </div>
  );
}
